import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";

import { AddressV2 } from "api/models";
import { getCompanyAddressV2Details } from "api/services/company";
import usePermissions from "hooks/usePermissions";
import { ScheduleStopValue } from "pages/shipper/RequestFormV2/defaults/scheduleDefaults";
import { StopAddressType } from "types/MultiStop";
import { useIsQuickRequest } from "@RequestFormV2/context/requestFormMode";
import { useSchedule } from "@RequestFormV2/sections/Schedule/context";
import { getStopFieldPath } from "@RequestFormV2/sections/Schedule/utils/fieldPaths";
import { getAddressTypeOptions } from "@RequestFormV2/sections/Schedule/utils/scheduleOptions";
import { isPortStopAddressType } from "@RequestFormV2/sections/Schedule/utils/stopTimezoneOptions";
import type { AddressTypeResolver } from "@RequestFormV2/sections/Schedule/hooks/useSharedAddress";
import type { AddressDialogConfig } from "@RequestFormV2/sections/Schedule/components/shared/AddressSection";
import type { PortOption } from "@RequestFormV2/sections/Schedule/utils/sharedAddressFieldPaths";
import {
  getStopSharedAddressFieldPaths,
  getStopSharedContactFieldPaths,
} from "@RequestFormV2/sections/Schedule/utils/stopAddressFieldPaths";

export function useStopAddressExtras(
  stopIndex: number,
  setAddressBookTimezone?: (timezone: string | null | undefined) => void,
  setPortTimezone?: (timezone: string | null | undefined) => void,
) {
  const intl = useIntl();
  const { getValues, setValue } = useFormContext();
  const isQuickRequest = useIsQuickRequest();
  const { location, scheduleOptions } = useSchedule();
  const { permissions }: any = usePermissions();
  const addressBookPermissions = permissions ? JSON.parse(permissions).permissions.quoting : null;

  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [dialogSelectedAddress, setDialogSelectedAddress] = useState<AddressV2 | undefined>(undefined);
  const portLinkedTimezoneRef = useRef<string | null>(null);

  const fieldPaths = useMemo(() => getStopSharedAddressFieldPaths(stopIndex), [stopIndex]);
  const contactFieldPaths = useMemo(() => getStopSharedContactFieldPaths(stopIndex), [stopIndex]);

  const addressTypeResolver: AddressTypeResolver = useMemo(() => {
    const options = getAddressTypeOptions(intl, scheduleOptions, { streetAddressOnly: isQuickRequest }).map((o) => ({
      value: String(o.value),
      label: o.label,
    }));
    return {
      options,
      isAirport: (v: unknown) => v === StopAddressType.AIRPORT,
      isSeaport: (v: unknown) => v === StopAddressType.SEAPORT,
      formatForRadio: (v: unknown) => String(v),
      parseFromRadio: (v: string) => Number(v),
    };
  }, [intl, isQuickRequest, scheduleOptions]);

  useEffect(() => {
    if (!isQuickRequest) return;

    const addressTypePath = getStopFieldPath(stopIndex, "stop_address_type");
    if (getValues(addressTypePath) === StopAddressType.ADDRESS) return;

    setValue(addressTypePath, StopAddressType.ADDRESS, { shouldDirty: true });
    setValue(getStopFieldPath(stopIndex, "stop_airport_code"), "");
    setValue(getStopFieldPath(stopIndex, "stop_airport_name"), "");
    setValue(getStopFieldPath(stopIndex, "stop_seaport_code"), "");
    setValue(getStopFieldPath(stopIndex, "stop_seaport_name"), "");
  }, [getValues, isQuickRequest, setValue, stopIndex]);

  const onAddressSelected = useCallback(
    (address: Record<string, unknown>) => {
      const currentStop = getValues(`schedule.stops.${stopIndex}`) as ScheduleStopValue;

      setValue(`schedule.stops.${stopIndex}`, {
        ...currentStop,
        address_id: (address.id as number | undefined) ?? currentStop.address_id,
        address_name: (address.name ?? address.label) as string,
        street_address: address.street_address,
        street_address_line_two: address.street_address_line_two,
        zipcode: address.zipcode,
        country_name: address.country_name,
        country_id: address.country_id ?? address.country,
        state_name: address?.state_name,
        state_id: address.state_id ?? address.state,
        city_name: address.city_name,
        city_id: address.city_id ?? address.city,
        address_reference_number: address.reference_number,
        un_locode: address.un_locode,
        location_reference_number: address.location_reference_number,
        custom_address_reference: address.custom_address_reference,
      });

      const addressTimezone = typeof address.timezone === "string" ? address.timezone.trim() : "";
      const portTimezone = portLinkedTimezoneRef.current;
      const hasPortSelected = isPortStopAddressType(currentStop.stop_address_type) && Boolean(portTimezone);

      if (hasPortSelected) {
        const addressDiffersFromPort =
          Boolean(addressTimezone) &&
          addressTimezone.toUpperCase() !== "UTC" &&
          addressTimezone !== portTimezone;
        setAddressBookTimezone?.(addressDiffersFromPort ? addressTimezone : null);
        return;
      }

      setAddressBookTimezone?.(addressTimezone || null);
    },
    [getValues, setAddressBookTimezone, setValue, stopIndex],
  );

  const onPortSelected = useCallback(
    (port: PortOption) => {
      setValue(getStopFieldPath(stopIndex, "port_country"), port.country_name || "");

      const linkedTimezone = port.address?.timezone?.trim() || "";
      portLinkedTimezoneRef.current = linkedTimezone || null;
      setPortTimezone?.(linkedTimezone || null);
      if (linkedTimezone) {
        setValue(getStopFieldPath(stopIndex, "stop_timezone"), linkedTimezone);
      }
    },
    [setPortTimezone, setValue, stopIndex],
  );

  const openAddAddressDialog = useCallback(() => {
    setDialogSelectedAddress(undefined);
    setShowAddressDialog(true);
  }, []);

  const openEditAddressDialog = useCallback(async () => {
    const stop = getValues(`schedule.stops.${stopIndex}`) as ScheduleStopValue;
    const referenceNumber =
      (getValues(fieldPaths.address_reference_number) as string | undefined) || stop.address_reference_number;
    const fallbackAddress = {
      ...stop,
      name: getValues(fieldPaths.address_name),
      reference_number: referenceNumber,
    } as AddressV2;

    if (!referenceNumber) {
      setDialogSelectedAddress(fallbackAddress);
      setShowAddressDialog(true);
      return;
    }

    try {
      const { data } = await getCompanyAddressV2Details(referenceNumber);
      const details: Record<string, unknown> = { ...(data as unknown as Record<string, unknown>) };
      ["city", "country", "state", "locode"].forEach((item) => {
        details[`${item}_id`] = details[`${item}_id`] ?? details[item];
      });
      setDialogSelectedAddress(details as AddressV2);
    } catch {
      setDialogSelectedAddress(fallbackAddress);
    }
    setShowAddressDialog(true);
  }, [getValues, stopIndex, fieldPaths]);

  const closeAddressDialog = useCallback(() => {
    setShowAddressDialog(false);
    setDialogSelectedAddress(undefined);
  }, []);

  const canAddAddressBookEntry = Boolean(addressBookPermissions?.allow_address_book_entry);

  const addressDialog: AddressDialogConfig | undefined = canAddAddressBookEntry
    ? {
        canAddAddressBookEntry,
        showAddressDialog,
        dialogSelectedAddress,
        location: location ?? undefined,
        openAddAddressDialog,
        openEditAddressDialog,
        closeAddressDialog,
      }
    : undefined;

  return {
    fieldPaths,
    contactFieldPaths,
    addressTypeResolver,
    onAddressSelected,
    onPortSelected,
    addressDialog,
    portLinkedTimezoneRef,
  };
}
